import ct from 'countries-and-timezones'

/**
 * Return the offset in minutes east of UTC for a given IANA zone at a given date.
 */
export function getOffsetMinutes(timeZone, when = new Date()) {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year:   "numeric",
    month:  "2-digit",
    day:    "2-digit",
    hour:   "2-digit",
    minute: "2-digit",
    second: "2-digit",
    timeZoneName: "shortOffset", // may not be supported everywhere
    hour12: false
  });

  const parts = dtf.formatToParts(when);
  let tzPart = parts.find(p => p.type === "timeZoneName")?.value || "";

  // Handle pure zero-offset labels and normalize prefixes
  if (tzPart === "GMT" || tzPart === "UTC") return 0;
  tzPart = tzPart.replace(/^(GMT|UTC)/, ""); // e.g. "+1", "+01:00", "-05"

  // Accept ±H, ±HH, ±HHMM, or ±HH:MM
  let m = tzPart.match(/^([+-])(\d{1,2})(?::?(\d{2}))?$/);
  if (m) {
    const sign = m[1] === "+" ? 1 : -1;
    const hours = parseInt(m[2], 10);
    const minutes = m[3] ? parseInt(m[3], 10) : 0;
    return sign * (hours * 60 + minutes);
  }

  // Fallback: compute by comparing the same 'when' in UTC vs target zone
  // (works across DST and engines without 'shortOffset')
  const utc = new Date(when.toLocaleString("en-US", { timeZone: "UTC", hour12: false }));
  const zoned = new Date(when.toLocaleString("en-US", { timeZone, hour12: false }));
  return Math.round((zoned.getTime() - utc.getTime()) / 60000);
}



/**
 * Return the difference in hours between zoneB and zoneA at a given date.
 * (i.e. offsetB – offsetA, in hours; positive means B is east of A)
 */
export function diffZonesInMinutes(zoneA, zoneB, when = new Date()) {
  const offA = getOffsetMinutes(zoneA, when);
  const offB = getOffsetMinutes(zoneB, when);
  return offB - offA;
}

/**
 * Given an IANA zone like "Europe/Paris",
 * returns the first 2-letter ISO country code (e.g. "FR").
 */
export function getCountryCodeForTimezone(zone) {
  // ct.getTimezone(zone) returns an object with a `countries` array.
  const info = ct.getTimezone(zone)
  if (!info) {
    throw new Error(`Unknown timezone: ${zone}`)
  }
  // Most zones map to exactly one country, but some are shared.
  // We’ll just return the first:
  return info.countries[0]
}

export function timeStringToSeconds(timeStr) {
    const [hours, minutes, seconds = 0] = timeStr.split(':').map(Number);
    return hours * 3600 + minutes * 60 + seconds;
}

export function secondsToTimeString(totalSeconds) {
  totalSeconds = ((totalSeconds % 86400) + 86400) % 86400;
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);
  const hh = String(hours).padStart(2, "0");
  const mm = String(minutes).padStart(2, "0");
  const ss = String(seconds).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
}

export function secondsToDurationString(totalSeconds) {
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${days}d ${hours}h ${minutes}m ${seconds.toFixed(1)}s`;
}

export function getNextAbsoluteTriggerTime(gameTime, secondsFromMidnight) {
    const SECONDS_PER_DAY = 86400;
    const dayStart = Math.floor(gameTime / SECONDS_PER_DAY) * SECONDS_PER_DAY;
    let trigger = dayStart + secondsFromMidnight;
    if (trigger <= gameTime) {
        trigger += SECONDS_PER_DAY; // schedule for next day if time already passed
    }
    return trigger;
}

export default { 
  getOffsetMinutes,
  diffZonesInMinutes,
  getCountryCodeForTimezone,
  timeStringToSeconds,
  secondsToTimeString,
  secondsToDurationString,
  getNextAbsoluteTriggerTime
};
